<template>
    <div
        class="user-avatar"
        :class="[`user-avatar--size-${size}`, fallbackClass]"
        :style="fallbackStyle"
        :data-test="dataTest || 'user-avatar'"
        :aria-label="ariaLabel"
        role="img"
    >
        <img
            v-if="hasAvatar"
            :src="resolvedAvatarUrl"
            :alt="ariaLabel"
            class="user-avatar__img"
            @error="handleImageError"
        />
        <span
            v-else-if="initials"
            class="user-avatar__initials"
            aria-hidden="true"
        >{{ initials }}</span>
        <v-icon
            v-else
            class="user-avatar__icon"
            :size="iconSize"
            aria-hidden="true"
        >mdi-account-outline</v-icon>
    </div>
</template>

<script setup lang="ts">
/**
 * UserAvatar — единая точка отрисовки аватара пользователя/наставника.
 *
 * Эта обёртка убирает разнобой между десятком разных мест, где у нас
 * раньше рисовался один и тот же блок: «есть avatar_url — <img>, нет —
 * что-нибудь страшное». Самым страшным был дефолтный
 * /default-avatar.svg (серый кружок с белой фигуркой человека) и
 * Vuetify'шный mdi-account-circle — оба читаются как «удалённый
 * аккаунт» и портили листинги/наставнические карточки/календарь.
 *
 * Стратегия фолбэка, по приоритету:
 *   1. avatar_url присутствует → <img>. URL предварительно проходит
 *      через getSafeAvatarUrl(), который ловит легаси-плейсхолдеры
 *      (testbucketplatforma34/blank.jpg и т.п.) и апскейлит
 *      Google-аватарки (см. shared/lib/avatar.ts).
 *   2. avatar отсутствует, но известно имя → круг с инициалами на
 *      фоновом цвете, выбранном детерминированно по hash(user.id).
 *      Тот же пользователь всегда получает тот же цвет — это даёт
 *      визуальную узнаваемость между сессиями.
 *   3. ни avatar, ни имени → нейтральный серый круг с
 *      mdi-account-outline (НЕ filled mdi-account-circle, который и
 *      был источником жалобы про «ублюдскую фотографию»).
 *
 * Размеры: 32 / 48 / 64 / 96 / 128 — соответствуют существующим UI
 * масштабам (мини-чип в чате, attendee-row, карточка эксперта, hero,
 * крупный preview в setup).
 */
import { computed, ref, watch } from 'vue';
import { getSafeAvatarUrl, DEFAULT_USER_AVATAR } from '@/shared/lib/avatar';

/**
 * Допустимые размеры. Жёсткий enum, чтобы не плодились случайные значения
 * (size="37" и т.п.) — все вызывающие должны лечь на дизайн-систему.
 */
export type UserAvatarSize = 32 | 48 | 64 | 96 | 128;

interface UserLike {
    id?: string | number | null;
    first_name?: string | null;
    last_name?: string | null;
    /**
     * Иногда профиль приходит с уже собранным полным именем (например, из
     * mentor list endpoint). Используется как fallback, когда нет
     * first_name/last_name отдельно.
     */
    full_name?: string | null;
    /**
     * Иногда (registrations, attendees) на бэке поле просто `name`.
     */
    name?: string | null;
    avatar_url?: string | null;
    /**
     * Альтернативное имя поля для аватара (в mentor-list endpoint поле
     * называется mentor_avatar_url).
     */
    mentor_avatar_url?: string | null;
}

const props = withDefaults(
    defineProps<{
        /**
         * Профиль пользователя. Может быть null/undefined в момент
         * загрузки — в этом случае рендерится нейтральный фолбэк.
         */
        user?: UserLike | null;
        /**
         * Явный override URL. Используется, когда профиля как такового
         * нет, а есть только готовая ссылка (например, в чате с
         * аватаркой собеседника). Если задан, перекрывает user.avatar_url.
         */
        avatarUrl?: string | null;
        /**
         * Явный override имени для расчёта инициалов. Полезно, когда мы
         * рисуем аватар по строке «Иван Петров» без полноценного user.
         */
        displayName?: string | null;
        /**
         * Размер кружка в пикселях. Дискретный набор — см. UserAvatarSize.
         */
        size?: UserAvatarSize;
        /**
         * data-test для интеграционных тестов.
         */
        dataTest?: string;
        /**
         * Альт-текст / aria-label. Если не задан, собирается из имени.
         */
        alt?: string;
    }>(),
    {
        user: null,
        avatarUrl: null,
        displayName: null,
        size: 48,
        dataTest: '',
        alt: '',
    }
);

/**
 * Фолбэк-фон — единый фирменный фиолетовый градиент.
 *
 * Альтернатива (палитра из 8 цветов по hash(user.id)) даёт визуальную
 * узнаваемость, но на практике превращается в «новогоднюю ёлку»: один
 * и тот же пользователь видит оранжевую аватарку в settings и зелёную
 * в header (разные id-источники), а каталог наставников выглядит как
 * радуга случайных кружков.
 *
 * Решение от продукта — единый бренд-градиент для всех аватаров
 * вместо palette+hash. Белые инициалы поверх 135°-фиолетового
 * (#7B65F7 → #9F8DFF) совпадают с CTA-кнопкой «Создать сессию» и
 * шапкой Share-блока, узнавание идёт через цвет бренда, а не
 * случайный hash.
 *
 * Дефолтный grey остаётся как нейтральный фолбэк, когда инициалов
 * посчитать не из чего (мы вообще не знаем, кто это).
 */
const BRAND_AVATAR_GRADIENT = 'linear-gradient(135deg, #7B65F7 0%, #9F8DFF 100%)';
const NEUTRAL_AVATAR_BG = '#94A3B8'; // slate-400 — для совсем «безымянных» аватаров

/**
 * Нормализация имени → 1-2 заглавные буквы инициалов.
 *
 * Источники имени, по приоритету:
 *   1. props.displayName (override)
 *   2. user.first_name + user.last_name
 *   3. user.full_name (склеенное на бэке имя)
 *   4. user.name (поле из attendees/registrations)
 *
 * Возвращает '' если имя пустое — это сигнал родителю рендерить
 * иконку-фолбэк вместо инициалов.
 */
const initials = computed<string>(() => {
    const explicit = (props.displayName ?? '').trim();
    if (explicit) {
        return computeInitialsFromName(explicit);
    }

    const u = props.user;
    if (u) {
        const first = (u.first_name ?? '').trim();
        const last = (u.last_name ?? '').trim();
        if (first || last) {
            const firstInitial = first.charAt(0);
            const lastInitial = last.charAt(0);
            return (firstInitial + lastInitial).toUpperCase();
        }
        const full = (u.full_name ?? u.name ?? '').trim();
        if (full) {
            return computeInitialsFromName(full);
        }
    }

    return '';
});

function computeInitialsFromName(name: string): string {
    const parts = name.trim().split(/\s+/);
    const first = parts[0]?.charAt(0) ?? '';
    const second = parts[1]?.charAt(0) ?? '';
    return (first + second).toUpperCase();
}

/**
 * URL аватара после прохождения через safety/upscale helper. Empty или
 * default-avatar.svg означают «нет реального аватара» — рисуем фолбэк.
 *
 * Также трекаем onerror-fallback: если <img> упал (битый URL, 404 на
 * S3 и т.п.), переключаемся на инициалы.
 */
const imageBroken = ref<boolean>(false);

const rawAvatarUrl = computed<string>(() => {
    if (props.avatarUrl != null) {
        return props.avatarUrl ?? '';
    }
    return props.user?.avatar_url ?? props.user?.mentor_avatar_url ?? '';
});

watch(rawAvatarUrl, () => {
    // Сбрасываем флаг при смене URL — может быть валидным.
    imageBroken.value = false;
});

const resolvedAvatarUrl = computed<string>(() => {
    const raw = rawAvatarUrl.value;
    if (!raw) return '';
    return getSafeAvatarUrl(raw);
});

const hasAvatar = computed<boolean>(() => {
    if (imageBroken.value) return false;
    const resolved = resolvedAvatarUrl.value;
    // getSafeAvatarUrl() возвращает DEFAULT_USER_AVATAR для пустых/легаси,
    // а наш фолбэк красивее дефолтной картинки — поэтому отбрасываем.
    if (!resolved || resolved === DEFAULT_USER_AVATAR) return false;
    return true;
});

const handleImageError = (): void => {
    imageBroken.value = true;
};

/**
 * Фон фолбэка:
 *   • если есть инициалы → бренд-градиент (`BRAND_AVATAR_GRADIENT`),
 *     один на все аватары — никаких разноцветных кружков, узнаём
 *     пользователя по инициалам, а не по случайному цвету;
 *   • если инициалов нет (вообще не знаем, кто) → нейтральный
 *     slate-400, чтобы заглушка не «кричала».
 *
 * Возвращаем строку CSS-значения (background-image / background-color),
 * `fallbackStyle` ниже сам решит, какое именно свойство задать.
 */
const fallbackBackground = computed<string>(() => {
    if (!initials.value) return NEUTRAL_AVATAR_BG;
    return BRAND_AVATAR_GRADIENT;
});

const fallbackClass = computed<string>(() => {
    if (hasAvatar.value) return 'user-avatar--has-img';
    if (initials.value) return 'user-avatar--has-initials';
    return 'user-avatar--has-icon';
});

const fallbackStyle = computed<Record<string, string>>(() => {
    if (hasAvatar.value) return {};
    if (initials.value) {
        // Градиент кладём в background (shorthand), чтобы не
        // конкурировать с возможным background-color из родительских
        // стилей и точно перебить дефолтный фон .user-avatar.
        return { background: fallbackBackground.value };
    }
    return { backgroundColor: fallbackBackground.value };
});

/**
 * Размер v-icon внутри иконочного фолбэка. Берём ~55% от размера
 * круга — это визуально центрирует иконку без вылета за края.
 */
const iconSize = computed<number>(() => Math.round(props.size * 0.55));

const ariaLabel = computed<string>(() => {
    if (props.alt) return props.alt;
    const u = props.user;
    const namePieces = [u?.first_name, u?.last_name].filter(Boolean).join(' ').trim();
    if (namePieces) return namePieces;
    if (u?.full_name) return u.full_name;
    if (u?.name) return u.name;
    if (props.displayName) return props.displayName;
    return 'Аватар пользователя';
});
</script>

<style scoped>
.user-avatar {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    overflow: hidden;
    flex-shrink: 0;
    color: #ffffff;
    font-family: 'Manrope', 'Inter', system-ui, sans-serif;
    font-weight: 700;
    letter-spacing: -0.01em;
    text-transform: uppercase;
    line-height: 1;
    user-select: none;
    /* Аватары — это «удостоверение личности», поэтому каждый кружок
       должен иметь чуть более явные грани, чем картинка-фон. Тонкая
       внутренняя обводка отделяет фолбэк-фон от соседнего bg карточки
       и не зависит от темы. */
    box-shadow: inset 0 0 0 1px rgba(15, 23, 42, 0.06);
}

.user-avatar__img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
}

.user-avatar__initials {
    /* Размер шрифта задаётся динамически через .user-avatar--size-N
       классы ниже — это удерживает все размерные правила в одном
       месте и облегчает добавление новых калибров. */
}

.user-avatar__icon {
    color: #ffffff;
    opacity: 0.92;
}

/* Размерная сетка. Шрифт инициалов = ~38% от диаметра круга — на
   этих числах одно- и двухбуквенные инициалы помещаются в равной
   высоте без обрезки на нижних/верхних апексах. */
.user-avatar--size-32 {
    width: 32px;
    height: 32px;
    font-size: 12px;
}

.user-avatar--size-48 {
    width: 48px;
    height: 48px;
    font-size: 18px;
}

.user-avatar--size-64 {
    width: 64px;
    height: 64px;
    font-size: 24px;
}

.user-avatar--size-96 {
    width: 96px;
    height: 96px;
    font-size: 34px;
}

.user-avatar--size-128 {
    width: 128px;
    height: 128px;
    font-size: 46px;
}

/* Когда есть картинка — обводка из аватара сама даёт край.
   В фолбэке усиливаем mostly-белым box-shadow по краям, чтобы
   аватар не «прилипал» к карточкам с цветным фоном (например,
   .attendee-row, у которой свой полупрозрачный фиолетовый bg). */
.user-avatar--has-initials,
.user-avatar--has-icon {
    box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.16);
}
</style>
